// Issue #422 (the workbooks) and #468 layer 3. Session 78u.
// Gate: docs/gates/Stede_ImportWorkbooks_Gate_422_468.html, decisions W1-W18.
//
// IN PLAIN TERMS: this builds the Excel file Stede hands somebody who has four
// hundred products to type in. Three sheets - how to use it, the sheet you type
// on, and the lists your choices come from - generated when the button is
// pressed, from the live database, so the lists inside it cannot be older than
// the moment it was downloaded.
//
// WHY THE FILE IS GENERATED RATHER THAN STORED. #465 is what happens otherwise:
// an Aug 19 template sat in the repository looking official, carrying 6 columns
// against the importer's 16. The columns here come from the same contract in
// masterDataTemplates.js that the importer reads, and so does every word of the
// guidance, so the file and the app cannot disagree.
//
// THINGS THAT LOOK LIKE STYLE AND ARE NOT:
//
//   NO CELL NOTES, ANYWHERE (W7). A worksheet carrying BOTH an Excel Table and
//   cell notes makes ExcelJS write the table reference before the notes
//   reference, which is the reverse of the order the file format requires.
//   Excel does not warn - it discards THE WHOLE SHEET and offers to repair the
//   file. Proven with an A/B pair and Excel's own repair log. The guidance
//   lives in the drop-down prompts instead, which is better placement anyway:
//   it shows on the cell you are typing in, on every row.
//
//   EXACT LIST RANGES, NEVER PADDED (W9). A range padded to A2:A500 swept the
//   sheet's own footnote into the Category drop-down and offered "Read from
//   Stede on 5 September 2026" as a pickable category.
//
//   THE HOUSE TYPEFACE IS NOT USED (W1). Barlow is a web font and a spreadsheet
//   carries no fonts inside it, so a workbook asking for Barlow falls back
//   silently to whatever the reader has. Arial Narrow is the nearest condensed
//   face that ships with Excel everywhere.
//
//   NO ERROR ALERTS (W6). Excel never refuses or warns about a typed value.
//   The lists are a download-time snapshot, so a warning built on them would
//   fire on values that are perfectly valid and train people to click through
//   it. Stede checks everything on upload and asks one question that fixes
//   every affected row.

// Contrast measured against white and against the ink colour rather than judged
// by eye (W2, W3). Every pair below clears 4.5:1 for body text, and the two
// title colours clear 3:1 at 26pt, which is the large-text threshold.
const INK = 'FF1F2421';
const GREY = 'FF6B7280';
const WHITE = 'FFFFFFFF';

export const PALETTES = {
  // Inventory. White on #177F5E is 4.96:1; ink on #A8D7C4 is 9.89:1;
  // brand #1D9E75 on white is 3.39:1, which passes at 26pt and nowhere else.
  inventory: {
    title: 'FF1D9E75',
    heading: 'FF177F5E',
    requiredFill: 'FF177F5E', requiredText: WHITE,
    optionalFill: 'FFA8D7C4', optionalText: INK,
  },
  // Purchasing. The domain colours are NOT interchangeable and assuming they
  // were would have shipped wrong: white on #EF9F27 is 2.17:1, failing even the
  // large-text bar, so orange is used as a FILL WITH DARK TEXT (7.25:1) and
  // never as text on white. The wordmark is ink and the section headings are a
  // deep amber that clears 4.5:1.
  purchasing: {
    title: INK,
    heading: 'FFA8660C',
    requiredFill: 'FFEF9F27', requiredText: INK,
    optionalFill: 'FFF8DCA8', optionalText: INK,
  },
};

const HEAD_FONT = 'Arial Narrow';
const BODY_FONT = 'Calibri';

const TABLE_ROWS = 40;      // pre-built rows; the Table extends past these
const VALIDATE_TO = 2000;

const SHEET_HOW = 'How to use this sheet';
const SHEET_LISTS = 'Lists';

function colLetter(i) {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  }
  return s;
}

function fmtDate(d) {
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ── The order a worksheet's parts must appear in ─────────────────────────────
//
// W18, and it is the whole reason this list is in the shipping code rather than
// only in a test. The defect it guards against passed every structural check
// available - the file was valid XML, every part parsed, nothing errored - and
// was caught only by Excel refusing to open it. One assertion turns a silently
// corrupt workbook into a failure we see.
export const WORKSHEET_ELEMENT_ORDER = [
  'sheetPr', 'dimension', 'sheetViews', 'sheetFormatPr', 'cols', 'sheetData',
  'sheetCalcPr', 'sheetProtection', 'protectedRanges', 'scenarios', 'autoFilter',
  'sortState', 'dataConsolidate', 'customSheetViews', 'mergeCells', 'phoneticPr',
  'conditionalFormatting', 'dataValidations', 'hyperlinks', 'printOptions',
  'pageMargins', 'pageSetup', 'headerFooter', 'rowBreaks', 'colBreaks',
  'customProperties', 'cellWatches', 'ignoredErrors', 'smartTags', 'drawing',
  'legacyDrawing', 'legacyDrawingHF', 'picture', 'oleObjects', 'controls',
  'webPublishItems', 'tableParts', 'extLst',
];

// Returns the offending element name, or null when the sheet is in order.
export function firstOutOfOrderElement(sheetXml) {
  const body = String(sheetXml).split('<worksheet')[1] || '';
  const seen = [];
  const re = /<(\/?)([a-zA-Z]+)[\s/>]/g;
  let m;
  while ((m = re.exec(body))) {
    if (m[1]) continue;
    const tag = m[2];
    if (WORKSHEET_ELEMENT_ORDER.includes(tag) && !seen.includes(tag)) seen.push(tag);
  }
  for (let i = 1; i < seen.length; i += 1) {
    if (WORKSHEET_ELEMENT_ORDER.indexOf(seen[i]) < WORKSHEET_ELEMENT_ORDER.indexOf(seen[i - 1])) {
      return seen[i];
    }
  }
  return null;
}

// ── Building the thing ───────────────────────────────────────────────────────
//
// `spec` is what a descriptor knows about its own workbook:
//   label     'Item catalog'
//   columns   the contract from masterDataTemplates.js, carrying needed/guide/help
//   lists     [{ header, values, for }] - `for` names the column key it feeds,
//             and a list with no `for` is context a person reads (the Under
//             column) rather than a source for a drop-down
//   palette   a key of PALETTES
//   tips      the three mistakes that cost the most time
//   company   the customer's name, from company_settings (W15)
export async function buildImportWorkbook(spec, { now = new Date() } = {}) {
  // Dynamically imported so the ~925 KiB library never enters the main bundle -
  // it is only needed by somebody who presses Download.
  const ExcelJS = (await import('exceljs')).default || (await import('exceljs'));
  const p = PALETTES[spec.palette] || PALETTES.inventory;
  const dated = fmtDate(now);
  const columns = spec.columns;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Stede';
  wb.created = now;

  // ── Sheet 1 · how to use this sheet ────────────────────────────────────────
  const how = wb.addWorksheet(SHEET_HOW, {
    views: [{ showGridLines: false }],
    pageSetup: {
      orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 },
    },
  });
  how.getColumn(1).width = 20;
  how.getColumn(2).width = 14;
  how.getColumn(3).width = 74;

  const put = (r, c, v, font) => {
    const cell = how.getCell(r, c);
    cell.value = v;
    cell.font = { name: BODY_FONT, size: 11, color: { argb: INK }, ...(font || {}) };
    cell.alignment = { vertical: 'top' };
    return cell;
  };
  const section = (r, text) =>
    put(r, 1, text, { name: HEAD_FONT, size: 12, bold: true, color: { argb: p.heading } });

  put(1, 1, 'STEDE', { name: HEAD_FONT, size: 26, bold: true, color: { argb: p.title } });
  put(2, 1, `${spec.label.toUpperCase()} IMPORT`, { name: HEAD_FONT, size: 16, bold: true });
  put(3, 1, `Prepared for ${spec.company}  |  lists current as of ${dated}`,
    { size: 10, italic: true, color: { argb: GREY } });

  section(5, 'WHAT THIS IS FOR');
  how.mergeCells('A6:C6');
  how.mergeCells('A7:C7');
  put(6, 1, `Type your products onto the ${spec.label} tab, one per row, then upload this file to Stede.`);
  put(7, 1, 'You do not need to save it as anything else. Stede reads this workbook directly.');

  section(9, 'THE THREE MISTAKES THAT COST THE MOST TIME');
  spec.tips.forEach((t, i) => {
    const r = 10 + i;
    how.mergeCells(`A${r}:C${r}`);
    put(r, 1, `${i + 1}.   ${t}`).alignment = { wrapText: true, vertical: 'top' };
  });

  const tableTop = 11 + spec.tips.length;
  section(tableTop, 'WHAT EACH COLUMN MEANS');
  ['Column', 'Needed?', 'What to put in it'].forEach((h, i) => {
    const cell = how.getCell(tableTop + 1, i + 1);
    cell.value = h;
    cell.font = { name: HEAD_FONT, size: 11, bold: true, color: { argb: p.requiredText } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: p.requiredFill } };
    cell.alignment = { vertical: 'middle' };
  });
  columns.forEach((col, i) => {
    const r = tableTop + 2 + i;
    put(r, 1, col.header, { bold: true });
    // W4 - required is carried by the WORD as well as the colour, because
    // Excel's own accessibility checker does not test for meaning conveyed by
    // colour alone. Verified; nothing automated would catch it.
    put(r, 2, col.needed, { bold: !!col.required, color: { argb: col.required ? p.heading : GREY } });
    put(r, 3, col.guide).alignment = { wrapText: true, vertical: 'top' };
    // No explicit row height. Setting one on a row that MIGHT wrap leaves a gap
    // under the rows that did not; Excel auto-fits a wrapped row on open.
  });

  const footRow = tableTop + 3 + columns.length;
  how.mergeCells(`A${footRow}:C${footRow}`);
  const foot = how.getCell(footRow, 1);
  foot.value =
    `About the drop-downs. They list your ${spec.listNoun} as they were on ${dated}. `
    + 'If somebody adds one in Stede after that, it will not be in this file, and if you paste '
    + 'into a cell Excel drops the drop-down for that cell entirely. That is fine. Stede checks '
    + 'every value again when you upload, and asks you about anything it does not recognise. '
    + 'The drop-downs are here to save you typing, not to be the safety net.';
  foot.font = { name: BODY_FONT, size: 10, italic: true, color: { argb: GREY } };
  foot.alignment = { wrapText: true, vertical: 'top' };
  how.getRow(footRow).height = 56;

  // ── Sheet 2 · the data sheet ───────────────────────────────────────────────
  const data = wb.addWorksheet(spec.label, {
    views: [{ state: 'frozen', ySplit: 1 }],
    pageSetup: {
      orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0,
      printTitlesRow: '1:1',      // W13 - headings repeat on every printed page
    },
  });

  // W5. A real Excel Table, so formatting and drop-downs extend as rows are
  // added. Without one, pasting a block from an old price list silently strips
  // both as it goes. `theme` MUST be a real style name - passing null writes a
  // style element with no name and Excel refuses the file.
  data.addTable({
    name: spec.label.replace(/[^A-Za-z0-9]/g, ''),
    ref: 'A1',
    headerRow: true,
    totalsRow: false,
    style: { theme: 'TableStyleLight8', showRowStripes: false },
    columns: columns.map(c => ({ name: c.header, filterButton: true })),   // W11
    rows: Array.from({ length: TABLE_ROWS }, () => columns.map(() => null)),
  });

  data.getRow(1).height = 26;
  columns.forEach((col, i) => {
    const cell = data.getCell(1, i + 1);
    cell.font = {
      name: HEAD_FONT, size: 12, bold: true,
      color: { argb: col.required ? p.requiredText : p.optionalText },
    };
    cell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: col.required ? p.requiredFill : p.optionalFill },
    };
    cell.alignment = { vertical: 'middle', wrapText: true };
    data.getColumn(i + 1).width = Math.max(14, Math.min(38, col.header.length + 10));
  });

  // Drop-downs. One per column, added at RANGE level: writing them cell by cell
  // makes ExcelJS emit OVERLAPPING ranges (D2:D3000 alongside D10:D3000,
  // because it sorts addresses as text) and Excel discards both, silently.
  const listByKey = new Map((spec.lists || []).filter(l => l.for).map(l => [l.for, l]));
  columns.forEach((col, i) => {
    const letter = colLetter(i);
    const range = `${letter}2:${letter}${VALIDATE_TO}`;
    const list = listByKey.get(col.key);

    if (list) {
      const listCol = colLetter((spec.lists || []).indexOf(list));
      const end = list.values.length + 1;          // W9 - exact, never padded
      if (end < 2) return;
      data.dataValidations.add(range, {
        type: 'list', allowBlank: true, showErrorMessage: false,   // W6
        showInputMessage: true, promptTitle: col.header, prompt: col.help,
        formulae: [`'${SHEET_LISTS}'!$${listCol}$2:$${listCol}$${end}`],
      });
      return;
    }

    if (col.kind === 'bool') {
      // W12. Yes/No is written into the file rather than read from Stede,
      // because nothing about it comes from the database.
      data.dataValidations.add(range, {
        type: 'list', allowBlank: true, showErrorMessage: false,
        showInputMessage: true, promptTitle: col.header, prompt: col.help,
        formulae: ['"Yes,No"'],
      });
      return;
    }

    // Every other column still gets its guidance, carried by a validation that
    // permits anything. This is the only way to put help on a cell without cell
    // notes, and cell notes are what break the file (W7).
    data.dataValidations.add(range, {
      type: 'textLength', operator: 'lessThan', formulae: [10000],
      allowBlank: true, showErrorMessage: false,
      showInputMessage: true, promptTitle: col.header, prompt: col.help,
    });
  });

  // ── Sheet 3 · Lists ────────────────────────────────────────────────────────
  const lists = wb.addWorksheet(SHEET_LISTS, {
    pageSetup: { orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  const listDefs = spec.lists || [];
  lists.addRow(listDefs.map(l => l.header));
  lists.getRow(1).eachCell(cell => {
    cell.font = { name: HEAD_FONT, size: 12, bold: true, color: { argb: p.requiredText } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: p.requiredFill } };
  });
  const depth = listDefs.reduce((n, l) => Math.max(n, l.values.length), 0);
  for (let r = 0; r < depth; r += 1) {
    lists.addRow(listDefs.map(l => (r < l.values.length ? l.values[r] : null)));
  }
  listDefs.forEach((l, i) => {
    const widest = l.values.reduce((n, v) => Math.max(n, String(v || '').length), l.header.length);
    lists.getColumn(i + 1).width = Math.max(16, Math.min(40, widest + 4));
  });

  // W10. The stamp sits BELOW the lists with a gap, where it reads as a footer.
  // Directly underneath, it was swept into a padded drop-down range; out to the
  // right, it detached from the data and pushed the sheet onto a second printed
  // page. It is safe here because the ranges above are exact.
  const stamp = lists.getCell(`A${depth + 4}`);
  stamp.value = `Read from Stede on ${dated}. Do not edit this sheet.`;
  stamp.font = { name: BODY_FONT, size: 10, italic: true, color: { argb: GREY } };
  await lists.protect('stede', { selectLockedCells: true, selectUnlockedCells: true });

  // BYTES, not a Blob. The caller wraps it for download; returning a Blob here
  // would make this module untestable outside a browser, and the checks that
  // matter most - W18's element order among them - are exactly the ones that
  // have to run in the test suite rather than in front of a person.
  return wb.xlsx.writeBuffer();
}

export const WORKBOOK_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Title-cased rather than taken from the label verbatim: "Item catalog" gives
// Stede_Item_catalog_Import.xlsx, which reads like a typo in a file somebody is
// about to email.
export function workbookFileName(label) {
  const words = String(label).split(/[^A-Za-z0-9]+/).filter(Boolean)
    .map(w => w[0].toUpperCase() + w.slice(1));
  return `Stede_${words.join('_')}_Import.xlsx`;
}
